import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	loading: false,
	error: null,
};

const extractMessages = (data) => {
	if (Array.isArray(data)) {
		return data;
	}
	if (Array.isArray(data?.messages)) {
		return data.messages;
	}
	if (Array.isArray(data?.result)) {
		return data.result;
	}
	return null;
};

const showSuccessMessages = (data, fallbackMessage) => {
	const messages = extractMessages(data);
	if (messages && messages.length) {
		messages.forEach((msg) => {
			if (msg?.status === "error") {
				toast.error(msg?.message || fallbackMessage?.error || "Request failed");
			} else {
				toast.success(msg?.message || fallbackMessage?.success || "Request successful");
			}
		});
	} else if (fallbackMessage?.success) {
		toast.success(fallbackMessage.success);
	}
};

const showErrorMessages = (errorData, fallbackMessage) => {
	const messages = extractMessages(errorData);
	if (messages && messages.length) {
		messages.forEach((msg) => toast.error(msg?.message || fallbackMessage));
	} else {
		toast.error(errorData?.message || fallbackMessage);
	}
};

const buildWBSPayload = ({
	WBSId = 0,
	ProjectId,
	ParentWBSId = 0,
	WBSName,
	WBSDescription,
	IsMilestone = false,
	StartDate,
	EndDate,
	ProgressPercent = 0,
	DataStatus = 1,
}) => ({
	WBSId,
	ProjectId,
	ParentWBSId,
	WBSName,
	WBSDescription,
	IsMilestone,
	StartDate,
	EndDate,
	ProgressPercent,
	DataStatus,
});

export const loadProjectWBS = createAsyncThunk(
	"projectWBS/load",
	async (projectId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("projects/ProjectWBSDetails", {
				params: projectId ? { ProjectId: projectId } : {},
			});
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load WBS details",
				}
			);
		}
	}
);

export const saveProjectWBS = createAsyncThunk(
	"projectWBS/save",
	async (payload, { rejectWithValue }) => {
		const requestBody = buildWBSPayload(payload ?? {});
		try {
			const { data } = await axios.post("projects/ProjectWBSDetailsSetup", requestBody);
			showSuccessMessages(data, {
				success: requestBody.WBSId ? "WBS updated" : "WBS added",
			});
			return data;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to save WBS");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to save WBS",
				}
			);
		}
	}
);

export const deleteProjectWBS = createAsyncThunk(
	"projectWBS/delete",
	async ({ ProjectId, WBSId }, { rejectWithValue }) => {
		try {
			const { data } = await axios.post("projects/ProjectWBSDetailsSetup", {
				ProjectId,
				WBSId,
				ParentWBSId: 0,
				WBSName: null,
				WBSDescription: null,
				IsMilestone: false,
				StartDate: null,
				EndDate: null,
				ProgressPercent: 0,
				DataStatus: 2,
			});
			showSuccessMessages(data, { success: "WBS deleted" });
			return data;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to delete WBS");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to delete WBS",
				}
			);
		}
	}
);

const projectWBSSlice = createSlice({
	name: "projectWBS",
	initialState,
	reducers: {
		clearProjectWBS: (state) => {
			state.list = [];
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadProjectWBS.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadProjectWBS.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadProjectWBS.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load WBS details";
			})
			.addCase(saveProjectWBS.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveProjectWBS.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(saveProjectWBS.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save WBS";
			})
			.addCase(deleteProjectWBS.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteProjectWBS.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(deleteProjectWBS.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to delete WBS";
			});
	},
});

export default projectWBSSlice.reducer;
export const { clearProjectWBS } = projectWBSSlice.actions;
